// Starter screen: drop a CSV, pick the fleet, build the plan.
//
// The file is checked as soon as it's chosen — bad rows surface in about a
// second instead of after a multi-minute solve, and they surface even when
// the solve later dies for reasons unrelated to the data. Findings inform;
// they never disable Build. The dispatcher may know something we don't.

import { useEffect, useRef, useState } from "react";

import { preflight } from "../api/client";
import type { FleetOptions } from "../api/client";
import { useT } from "../lib/i18n";
import type { PreflightIssue } from "../types";
import { TruckDepartures, useTruckDepartures } from "./TruckDepartures";

export function UploadView({
  onSubmit,
  onEvaluate,
  errorMessage,
}: {
  onSubmit: (file: File, fleet: FleetOptions) => void;
  onEvaluate: (file: File, fleet: FleetOptions) => void;
  errorMessage: string | null;
}) {
  const t = useT();
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [num26ft, setNum26ft] = useState(3);
  const [num53ft, setNum53ft] = useState(0);
  const [num3pl, setNum3pl] = useState(0);
  const departures = useTruckDepartures();
  const fleet = {
    num_26ft: num26ft,
    num_53ft: num53ft,
    departures: departures.list(num26ft, num53ft),
    num_3pl: num3pl,
    threepl_departures: departures.dep3pl.slice(0, num3pl),
  };
  // Pre-flight runs on the file and on any fleet change, because the same
  // pallet can be a blocker with three 26 ft trucks and fine with a 53 ft.
  // null = not checked yet; [] = checked and clean.
  const [issues, setIssues] = useState<PreflightIssue[] | null>(null);
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function pick(f: File | undefined) {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) return;
    setFile(f);
  }

  useEffect(() => {
    if (!file) {
      setIssues(null);
      return;
    }
    let live = true;
    setChecking(true);
    preflight(file, fleet)
      // A failed check must never block the build — it is an extra, not a
      // gate. Fall back to showing nothing.
      .catch(() => [] as PreflightIssue[])
      .then((found) => live && setIssues(found))
      .finally(() => live && setChecking(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `fleet` is rebuilt each render
  }, [file, num26ft, num53ft, departures.dep26, departures.dep53]);

  const blockers = (issues ?? []).filter((i) => i.severity === "blocker");
  const warnings = (issues ?? []).filter((i) => i.severity === "warning");

  return (
    <section className="starter">
      <div className="starter-card">
        <h1>{t("upload.heading")}</h1>
        <p className="starter-desc">{t("upload.desc")}</p>

        <div
          className={`file-drop${dragOver ? " drag-over" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            pick(e.dataTransfer.files?.[0]);
          }}
        >
          <div className="file-drop-icon">⤓</div>
          <div className="file-drop-label">{file ? file.name : t("upload.drop")}</div>
          <div className="file-drop-sublabel">{t("upload.cols")}</div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={(e) => pick(e.target.files?.[0])}
        />

        <div className="config-row">
          <Select label={t("upload.fleet26")} value={num26ft} set={setNum26ft} max={10} />
          <Select label={t("upload.fleet53")} value={num53ft} set={setNum53ft} max={3} />
          <Select label={t("upload.fleet3pl")} value={num3pl} set={setNum3pl} max={4} />
        </div>
        <TruckDepartures
          num26ft={num26ft}
          num53ft={num53ft}
          num3pl={num3pl}
          departures={departures}
        />

        {checking && <div className="preflight-checking">{t("upload.checking")}</div>}
        {blockers.length > 0 && (
          <div className="banner warn">
            {t("upload.blockers", { n: blockers.length })}
            <ul>
              {blockers.map((i, k) => (
                <li key={`b${k}`}>
                  {i.shipment_id} → {i.dest_zip} ({i.reason})
                </li>
              ))}
            </ul>
          </div>
        )}
        {warnings.length > 0 && (
          <div className="banner warn">
            {t("upload.warnings", { n: warnings.length })}
            <ul>
              {warnings.map((i, k) => (
                <li key={`w${k}`}>
                  {i.shipment_id} → {i.dest_zip} ({i.reason})
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="starter-actions">
          <button
            className="btn btn-primary btn-lg"
            disabled={!file || num26ft + num53ft < 1}
            onClick={() => file && onSubmit(file, fleet)}
          >
            {t("upload.build")}
          </button>
          <button
            className="btn btn-secondary btn-lg"
            disabled={!file}
            title={t("upload.evaluateTip")}
            onClick={() => file && onEvaluate(file, fleet)}
          >
            {t("upload.evaluate")}
          </button>
          {errorMessage && <span className="upload-status error">{errorMessage}</span>}
        </div>
        <p className="starter-desc" style={{ marginTop: 8, fontSize: 13, opacity: 0.75 }}>
          {t("upload.evaluateExplainer")}
        </p>
      </div>
    </section>
  );
}

function Select({
  label,
  value,
  set,
  max,
}: {
  label: string;
  value: number;
  set: (n: number) => void;
  max: number;
}) {
  return (
    <label className="control">
      <span className="control-label">{label}</span>
      <select value={value} onChange={(e) => set(Number(e.target.value))}>
        {Array.from({ length: max + 1 }, (_, i) => (
          <option key={i} value={i}>
            {i}
          </option>
        ))}
      </select>
    </label>
  );
}
